"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import { commonApiFetch } from "@/services/api/common-api";
import { DropSize, type ExtendedDrop } from "@/helpers/waves/drop.helpers";
import type { WsDropUpdateMessage } from "@/helpers/Types";
import { WsMessageType } from "@/helpers/Types";
import { useWebSocketMessage } from "@/services/websocket/useWebSocketMessage";
const GALLERY_DROPS_LIMIT = 20;
const processDrops = (pages: ApiDrop[][] | undefined) => {
  if (!pages) return [];
  // Flatten all pages into single array and convert to ExtendedDrop
  const allDrops = pages.flat();
  const extendedDrops: ExtendedDrop[] = allDrops.map(drop => ({
    ...drop,
    type: DropSize.FULL,
    stableKey: drop.id,
    stableHash: drop.id
  }));
  const keyCount = new Map<string, number>();
  return extendedDrops.map(drop => {
    const count = (keyCount.get(drop.stableKey) ?? 0) + 1;
    keyCount.set(drop.stableKey, count);
    if (count > 1) {
      return {
        ...drop,
        stableKey: `${drop.stableKey}-${count}`
      };
    }
    return drop;
  });
};
export function useWaveGalleryDrops(waveId) {
  const $ = _c(37);
  let t0;
  if ($[0] !== waveId) {
    t0 = [QueryKey.DROPS, {
      waveId,
      limit: GALLERY_DROPS_LIMIT,
      containsMedia: true
    }];
    $[0] = waveId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const queryKey = t0;
  let t1;
  if ($[2] !== waveId) {
    t1 = async t2 => {
      const {
        pageParam
      } = t2;
      const params = {
        wave_id: waveId,
        limit: GALLERY_DROPS_LIMIT.toString(),
        contains_media: "true"
      };
      if (typeof pageParam === "number") {
        params.serial_no_less_than = `${pageParam}`;
      }
      return await commonApiFetch({
        endpoint: "drops",
        params
      });
    };
    $[2] = waveId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const t2 = !!waveId;
  let t3;
  if ($[4] !== queryKey || $[5] !== t1 || $[6] !== t2) {
    t3 = {
      queryKey,
      queryFn: t1,
      enabled: t2,
      initialPageParam: null,
      getNextPageParam: _temp,
      placeholderData: keepPreviousData,
      staleTime: 60000,
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      refetchOnReconnect: true
    };
    $[4] = queryKey;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const {
    data,
    fetchNextPage: onFetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    refetch
  } = useInfiniteQuery(t3);
  let t4;
  if ($[8] !== hasNextPage || $[9] !== isFetchingNextPage || $[10] !== onFetchNextPage) {
    t4 = async () => {
      if (hasNextPage && !isFetchingNextPage) {
        await onFetchNextPage();
      }
    };
    $[8] = hasNextPage;
    $[9] = isFetchingNextPage;
    $[10] = onFetchNextPage;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const fetchNextPage = t4;
  const t5 = data?.pages;
  let t6;
  if ($[12] !== t5) {
    t6 = processDrops(t5);
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const drops = t6;
  const lastRefetchTimeRef = useRef(0);
  const pendingRefetchRef = useRef(false);
  const timeoutRef = useRef(null);
  const isFetchingRef = useRef(isFetching);
  const isFetchingNextPageRef = useRef(isFetchingNextPage);
  let t7;
  let t8;
  if ($[14] !== isFetching || $[15] !== isFetchingNextPage) {
    t7 = () => {
      isFetchingRef.current = isFetching;
      isFetchingNextPageRef.current = isFetchingNextPage;
    };
    t8 = [isFetching, isFetchingNextPage];
    $[14] = isFetching;
    $[15] = isFetchingNextPage;
    $[16] = t7;
    $[17] = t8;
  } else {
    t7 = $[16];
    t8 = $[17];
  }
  useEffect(t7, t8);
  let t9;
  if ($[18] !== isFetching || $[19] !== isFetchingNextPage || $[20] !== refetch) {
    t9 = () => {
      const now = Date.now();
      const timeSinceLastRefetch = now - lastRefetchTimeRef.current;
      const executeRefetch = () => {
        lastRefetchTimeRef.current = Date.now();
        pendingRefetchRef.current = false;
        refetch().catch(_temp2);
      };
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (isFetching || isFetchingNextPage) {
        pendingRefetchRef.current = true;
        return;
      }
      if (timeSinceLastRefetch < 1000) {
        pendingRefetchRef.current = true;
        timeoutRef.current = setTimeout(() => {
          timeoutRef.current = null;
          if (isFetchingRef.current || isFetchingNextPageRef.current) {
            return;
          }
          executeRefetch();
        }, 1000 - timeSinceLastRefetch);
        return;
      }
      executeRefetch();
    };
    $[18] = isFetching;
    $[19] = isFetchingNextPage;
    $[20] = refetch;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const onRefetch = t9;
  let t10;
  let t11;
  if ($[22] !== isFetching || $[23] !== isFetchingNextPage || $[24] !== refetch) {
    t10 = () => {
      if (!isFetching && !isFetchingNextPage && pendingRefetchRef.current) {
        const now_0 = Date.now();
        const timeSinceLastRefetch_0 = now_0 - lastRefetchTimeRef.current;
        if (timeSinceLastRefetch_0 >= 1000) {
          lastRefetchTimeRef.current = now_0;
          pendingRefetchRef.current = false;
          refetch().catch(_temp3);
        } else {
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
          }
          timeoutRef.current = setTimeout(() => {
            timeoutRef.current = null;
            if (pendingRefetchRef.current) {
              lastRefetchTimeRef.current = Date.now();
              pendingRefetchRef.current = false;
              refetch().catch(_temp4);
            }
          }, 1000 - timeSinceLastRefetch_0);
        }
      }
      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
      };
    };
    t11 = [isFetching, isFetchingNextPage, refetch];
    $[22] = isFetching;
    $[23] = isFetchingNextPage;
    $[24] = refetch;
    $[25] = t10;
    $[26] = t11;
  } else {
    t10 = $[25];
    t11 = $[26];
  }
  useEffect(t10, t11);
  let t12;
  if ($[27] !== onRefetch || $[28] !== waveId) {
    t12 = message => {
      if (waveId !== message.wave.id) {
        return;
      }
      onRefetch();
    };
    $[27] = onRefetch;
    $[28] = waveId;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  useWebSocketMessage(WsMessageType.DROP_UPDATE, t12);
  let t13;
  if ($[30] !== drops || $[31] !== fetchNextPage || $[32] !== hasNextPage || $[33] !== isFetching || $[34] !== isFetchingNextPage || $[35] !== refetch) {
    t13 = {
      drops,
      fetchNextPage,
      hasNextPage,
      isFetching,
      isFetchingNextPage,
      refetch
    };
    $[30] = drops;
    $[31] = fetchNextPage;
    $[32] = hasNextPage;
    $[33] = isFetching;
    $[34] = isFetchingNextPage;
    $[35] = refetch;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  return t13;
}
function _temp4() {}
function _temp3() {}
function _temp2() {}
function _temp(lastPage) {
  return lastPage.at(-1)?.serial_no ?? null;
}